import { useTranslation } from 'react-i18next';
import { CreditCard, Wallet, Lock } from 'lucide-react';
import type { StartPreviewResponse } from '@/lib/api';
import { toast } from '@/store/toast';
import { formatMoneyExplicit } from '@/lib/money';
import NamedIcon from '@/components/ui/NamedIcon';
import { walletBalanceCopy, walletOptionView, walletRateCopy, walletStateCopy } from './walletOptionView';
import { paymentChecked, paymentTap, walletTopUpCopy, type PaymentSource } from './paymentChoice';
import styles from './PaymentSelector.module.css';

interface Props {
  preview: StartPreviewResponse;
  /** From /billing/payment-methods. May be undefined while loading. */
  cardLast4: string | null;
  cardBrand: string | null;
  /** Driver tapped a usable option. Parent persists + updates UI. */
  onSelect: (source: PaymentSource) => void;
  /** Currently selected source, or null when neither can start this
   *  session. Parent owns this state for optimistic UI. */
  selected: PaymentSource | null;
  /** The wallet cannot pay yet and the driver tapped it: its reason and
   *  the "Top up" action are open. Parent owns it, beside `selected`. */
  walletExplained: boolean;
  /** Driver tapped a wallet that cannot pay yet. */
  onExplainWallet: () => void;
  /** Used by toasts/links to navigate. */
  onNavigate: (path: string) => void;
}

/**
 * The payment choice on the screen before a start: Card and Wallet as two
 * radio options, stacked, the selected one unmistakable (filled radio,
 * accent border). It reflects the gate's view of which sources can pay for
 * THIS driver right now:
 *
 *   - Card names the card ("Mastercard ending 0416") with a "Change" link
 *     to payment methods ("Add a card" with none). It replaces the separate
 *     "Payment method" row that used to sit above the selector. Locked:
 *     the tap says until when. No card: the tap goes to add one.
 *   - Wallet: ALWAYS shown (Warren, 2026-10-25), with its balance in its
 *     own currency and either "Use wallet" or the exact reason it can't be
 *     used yet. Selectable exactly when the start gate would resolve to
 *     wallet (the preview carries the gate's own option, walletOptionView).
 *
 * Card and Wallet toggle freely while both can pay; selecting never
 * navigates (paymentChoice.ts). A wallet that cannot pay yet is never
 * selected: the tap opens its reason with a "Top up" action (the wallet
 * page in the currency and amount the start needs) when a top-up fixes it.
 * Only a usable selection calls onSelect, so the stored preference only
 * changes when it is actionable.
 */
export function PaymentSelector({
  preview, cardLast4, cardBrand, onSelect, selected, walletExplained, onExplainWallet, onNavigate,
}: Props) {
  const { t, i18n } = useTranslation('preSession');
  const cardEnabled = preview.card.available;
  // The gate's own wallet option: selectable exactly when the start would
  // take wallet mode. The wallet is labelled in ITS currency (item 3),
  // never the price's, and always says which dollar ("US$", "CA$").
  const wallet = walletOptionView(preview);
  const walletEnabled = wallet.selectable;
  const ctx = { cardEnabled, cardLockedOut: preview.card.lockedOut, walletSelectable: walletEnabled };
  const checked = paymentChecked(selected, ctx);
  const explained = walletExplained && !walletEnabled;
  const money = (cents: number, currency: string): string => formatMoneyExplicit(cents, currency, i18n.language);
  const walletState = walletStateCopy(wallet, money);

  const cardSubtitle = (() => {
    if (preview.card.lockedOut) return t('selector.cardLocked');
    if (!preview.card.hasCardOnFile) return t('selector.noPaymentMethod');
    if (cardBrand && cardLast4) return t('selector.cardEndingBrand', { brand: cardBrand, last4: cardLast4 });
    if (cardLast4) return t('selector.cardEnding', { last4: cardLast4 });
    return t('selector.cardOnFile');
  })();

  // A USD wallet at a CAD charger shows both currencies and the rate the
  // start would convert at (2026-10-25T02:52:41Z ruling).
  const balanceCopy = walletBalanceCopy(wallet, money);
  const walletBalance = t(balanceCopy.key, balanceCopy.values);
  const rateCopy = walletRateCopy(wallet);
  const walletStateText = t(walletState.key, walletState.values);
  const topUpCopy = walletTopUpCopy(wallet, money);
  const topUpPath = wallet.topUpPath;

  const handleTap = (option: PaymentSource) => {
    const tap = paymentTap(option, ctx);
    switch (tap.kind) {
      case 'select':
        onSelect(tap.source);
        return;
      case 'explainWallet':
        // Say why here, with the action that fixes it. No navigation.
        onExplainWallet();
        return;
      case 'cardLocked': {
        const until = preview.card.lockoutUntil ? new Date(preview.card.lockoutUntil) : null;
        const relative = until ? formatRelativeFuture(until) : 'soon';
        toast(t('selector.toastCardLockedUntil', { relative }), { tone: 'warn' });
        return;
      }
      case 'addCard':
        // No PM on file: route to billing.
        toast(t('selector.toastAddCard'), { tone: 'neutral' });
        onNavigate('/billing/payment-methods');
        return;
    }
  };

  return (
    <div
      className={styles.selector}
      role="radiogroup"
      aria-label={t('selector.a11yPaymentSource')}
    >
      <div
        className={[
          styles.item,
          checked.card ? styles.itemSelected : '',
          !cardEnabled ? styles.itemDisabled : '',
        ].filter(Boolean).join(' ')}
        data-payment-option="card"
        data-selected={checked.card ? 'true' : 'false'}
      >
        <button
          type="button"
          role="radio"
          aria-checked={checked.card}
          aria-disabled={!cardEnabled}
          className={styles.option}
          onClick={() => handleTap('card')}
        >
          <span className={styles.radio} aria-hidden />
          <span className={styles.optionIcon}>
            {preview.card.lockedOut
              ? <Lock size={16} />
              : <NamedIcon name="creditcard" fallback={<CreditCard size={16} />} size={16} />}
          </span>
          <span className={styles.optionBody}>
            <span className={styles.optionLabel}>{t('selector.cardLabel')}</span>
            <span className={styles.optionSubtitle}>{cardSubtitle}</span>
          </span>
        </button>
        {/* The old "Payment method" row's job: change (or add) the card. */}
        <button
          type="button"
          className={styles.inlineAction}
          data-card-action={preview.card.hasCardOnFile ? 'change' : 'add'}
          aria-label={preview.card.hasCardOnFile ? t('selector.changeCardA11y') : undefined}
          onClick={() => onNavigate('/billing/payment-methods')}
        >
          {preview.card.hasCardOnFile ? t('selector.changeCard') : t('gate.addCard')}
        </button>
      </div>

      <div
        className={[
          styles.item,
          checked.wallet ? styles.itemSelected : '',
          !walletEnabled ? styles.itemExplained : '',
          explained ? styles.itemOpen : '',
        ].filter(Boolean).join(' ')}
        data-payment-option="wallet"
        data-selected={checked.wallet ? 'true' : 'false'}
        data-wallet-reason={wallet.reason ?? 'usable'}
      >
        <button
          type="button"
          role="radio"
          aria-checked={checked.wallet}
          aria-disabled={!walletEnabled}
          aria-describedby={explained ? 'evi-wallet-why' : undefined}
          className={styles.option}
          onClick={() => handleTap('wallet')}
        >
          <span className={styles.radio} aria-hidden />
          <span className={styles.optionIcon}>
            <NamedIcon name="wallet" fallback={<Wallet size={16} />} size={16} />
          </span>
          <span className={styles.optionBody}>
            <span className={styles.optionLabel}>{t('selector.walletLabel')}</span>
            <span className={[styles.optionSubtitle, styles.optionBalance, rateCopy ? styles.optionBalanceConverted : '']
              .filter(Boolean).join(' ')}>{walletBalance}</span>
            {rateCopy && (
              <span className={`${styles.optionSubtitle} ${styles.optionRate}`} data-wallet-rate={rateCopy.values.rate}>
                {t(rateCopy.key, rateCopy.values)}
              </span>
            )}
            {!explained && (
              <span
                className={[
                  styles.optionState,
                  walletEnabled ? styles.optionStateUse : wallet.topUpPath ? styles.optionStateAction : '',
                ].filter(Boolean).join(' ')}
              >
                {walletStateText}
              </span>
            )}
          </span>
        </button>
        {explained && (
          <div id="evi-wallet-why" className={styles.explain} role="status" data-wallet-explained="true">
            <p className={styles.explainText}>{walletStateText}</p>
            {topUpCopy && topUpPath && (
              <button
                type="button"
                className={styles.topUpBtn}
                data-wallet-topup={topUpPath}
                onClick={() => onNavigate(topUpPath)}
              >
                {t(topUpCopy.key, topUpCopy.values)}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────

function formatRelativeFuture(date: Date): string {
  const diffMs = date.getTime() - Date.now();
  if (diffMs <= 0) return 'soon';
  const days = Math.floor(diffMs / (24 * 60 * 60 * 1000));
  if (days >= 2) {
    // Surface a calendar date when far enough out: driver can act on it.
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }
  if (days === 1) return 'tomorrow';
  const hours = Math.ceil(diffMs / (60 * 60 * 1000));
  return `in ${hours} ${hours === 1 ? 'hour' : 'hours'}`;
}
